import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import {
  KEY_MAP,
  KeyTypeValue,
  KeyHandler,
  Subscribers,
  KeyDownContextType,
} from './types'

const findKeyEvent = (event: KeyboardEvent) => {
  const eventKey = event.key
  const eventKeyCode = event.keyCode
  for (const [key, value] of Object.entries(KEY_MAP)) {
    if (value.codes.includes(eventKeyCode)) {
      return key
    } else if (value.keys.includes(eventKey)) {
      return key
    }
  }
  return null
}

const KeyDownContext = createContext<KeyDownContextType | undefined>(undefined)

const KeyDownProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const subscribers = useRef<Subscribers>({})

  const keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const eventKey = findKeyEvent(event)
      if (!eventKey) {
        const keyEvent = event.key || event.keyCode
        console.warn(`Key event not found in KEY_MAP: ${keyEvent}`)
        return
      }

      const currentHandlers = [
        ...(subscribers.current[eventKey as KeyTypeValue] || []),
      ]

      const stopPropagationHandler = currentHandlers.find(
        (singleHandler) => singleHandler.options?.stopPropagation
      )
      if (stopPropagationHandler) {
        stopPropagationHandler.callback()
        return
      }

      for (const { callback } of currentHandlers) {
        callback(eventKey as KeyTypeValue)
      }
    },
    [subscribers.current]
  )

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler)
    return () => {
      window.removeEventListener('keydown', keyDownHandler)
    }
  }, [keyDownHandler])

  const subscribe = useCallback(
    (key: KeyTypeValue, handler: KeyHandler): (() => void) => {
      subscribers.current = {
        ...subscribers.current,
        [key]: [...(subscribers.current[key] || []), handler],
      }

      return () => {
        const currentHandlers = [...(subscribers.current[key] || [])]
        if (currentHandlers) {
          subscribers.current = {
            ...subscribers.current,
            [key]: currentHandlers.filter(
              (singleHandler) => singleHandler.callback !== handler.callback
            ),
          }

          if (currentHandlers.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [key]: _, ...rest } = subscribers.current
            subscribers.current = rest
          }
        }
      }
    },
    [subscribers.current]
  )

  return (
    <KeyDownContext.Provider value={{ subscribe }}>
      {children}
    </KeyDownContext.Provider>
  )
}

export const useKeyDown = (
  keys: KeyTypeValue | KeyTypeValue[],
  callback: (key?: KeyTypeValue) => void,
  options?: { stopPropagation?: boolean }
) => {
  const context = useContext(KeyDownContext)

  if (!context) {
    throw new Error('useKeyDown must be used within a KeyDownProvider')
  }

  const { subscribe } = context

  const memoizedCallback = useCallback(callback, [callback])

  useEffect(() => {
    const keyArray = Array.isArray(keys) ? keys : [keys]
    const unsubscribes = keyArray.map((key) =>
      subscribe(key, { callback: memoizedCallback, options })
    )
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe())
    }
  }, [keys, memoizedCallback, options, subscribe])
}

export default KeyDownProvider
